import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';
import { Flex, theme } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { useClientByIdQuery, useClientEmployeesQuery } from '@/api/auth/queries';
import {
  useIssueTenantRoleTokenForClientMutation,
  useDeleteCompanyMutation,
  useUpdateAnyCompanyMemberRoleMutation,
  useRemoveAnyCompanyMemberMutation,
  useRemoveClientMemberMutation
} from '@/api/auth/mutations';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveClientId } from '@/contexts/AdminClientsContext';
import type { UserWithClientMembership } from '@/api/auth/interfaces';
import { TenantInviteModal } from '@/components/auth/TenantInviteModal';
import { CompanyModal } from '@/components/tenant/CompanyModal';
import { TenantCompaniesTable, AddCompanyMemberModal } from '@/components/tenant';
import { EmployeesTable, EmployeesEmptyState } from '@/components/employees';
import { App } from 'antd';
import { PageHeader } from '@/components/ui/PageHeader';
import { createManageRouteGuard } from '@/utils/manageRouteGuards';
import { TENANT_ROLES, type TenantRoleType } from '@/constants/tenantRoles';
import { isAdminEquivalentRole } from '@/utils/authz';
import { useCompanyManagement } from '@/hooks/useCompanyManagement';

export const Route = createFileRoute('/manage/employees')({
  beforeLoad: createManageRouteGuard({ requiredTenantRoles: [TENANT_ROLES.OWNER] }),
  component: EmployeesPage
});

function EmployeesPage() {
  const { t } = useTranslation('manage');
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const { payload } = useAuth();
  const clientId = useEffectiveClientId();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const [addToCompanyUser, setAddToCompanyUser] = useState<UserWithClientMembership | null>(null);

  const isAdmin = isAdminEquivalentRole(payload?.role);

  const clientQuery = useClientByIdQuery(clientId ?? '', !!clientId);
  const client = clientQuery.data?.client;
  const companies = client?.companies ?? [];

  const employeesQuery = useClientEmployeesQuery(clientId ?? '', !!clientId);
  const issueTokenMutation = useIssueTenantRoleTokenForClientMutation(clientId ?? '');
  const deleteCompanyMutation = useDeleteCompanyMutation(clientId ?? '');
  const updateCompanyRoleMutation = useUpdateAnyCompanyMemberRoleMutation(clientId ?? '');
  const removeCompanyMemberMutation = useRemoveAnyCompanyMemberMutation(clientId ?? '');
  const removeClientMemberMutation = useRemoveClientMemberMutation(clientId ?? '');
  const companyManagement = useCompanyManagement(clientId, {
    created: t('employees.companyCreateSuccess', 'Company created'),
    updated: t('employees.companyUpdateSuccess', 'Company updated'),
    createError: (error) => error?.message ?? 'Failed to create company',
    updateError: (error) => error?.message ?? 'Failed to update company'
  });

  const users = employeesQuery.data?.users ?? [];
  const total = employeesQuery.data?.total ?? 0;

  /** Issues an invite token for the tenant (or the given company). */
  const handleGenerateInvite = async (params: {
    tenantRole: TenantRoleType;
    companyId?: string;
  }) => {
    return issueTokenMutation.mutateAsync(params);
  };

  /** Resets the employees table search and role filter. */
  const handleClearFilters = () => {
    setSearch('');
    setRoleFilter(undefined);
  };

  if (!clientId) {
    return <EmployeesEmptyState isAdmin={!!isAdmin} />;
  }

  const inviteContextTitle =
    companyManagement.inviteContext.type === 'client'
      ? t('employees.invite.contextTitle', 'Invite a team member')
      : t('employees.invite.contextTitleCompany', {
          company: companies.find((c) => c.id === companyManagement.inviteContext.companyId)?.name ?? client?.name ?? ''
        });

  return (
    <div className="manage-fade-in">
      <PageHeader
        icon={<TeamOutlined />}
        title={t('employees.companiesAndEmployees', 'Companies / Employees')}
        description={t('employees.companiesAndEmployeesDescription', 'Manage your companies and team members.')}
      />

      <Flex vertical gap={token.marginLG}>
        <TenantCompaniesTable
          companies={companies}
          onAddCompany={companyManagement.openAddCompany}
          onEditCompany={companyManagement.openEditCompany}
          onGenerateInviteLink={(company) => companyManagement.openInviteModal('company', company.id)}
          onDeleteCompany={(company) => {
            deleteCompanyMutation.mutate(company.id, {
              onSuccess: () => message.success(t('employees.companyDeleteSuccess', 'Company deleted')),
              onError: (err: Error) => message.error(err?.message ?? t('employees.companyDeleteError', 'Failed to delete company'))
            });
          }}
          loading={clientQuery.isLoading}
        />
        <EmployeesTable
          users={users}
          total={total}
          search={search}
          roleFilter={roleFilter}
          onSearchChange={setSearch}
          onRoleFilterChange={setRoleFilter}
          onClearFilters={handleClearFilters}
          isLoading={employeesQuery.isLoading}
          clientId={clientId}
          currentUserId={payload?.userId}
          canAddMemberFromList={false}
          clientCompanies={companies}
          onAddToCompanyClick={(user) => setAddToCompanyUser(user)}
          onChangeCompanyRole={(userId, companyId, role) =>
            updateCompanyRoleMutation.mutate({ companyId, userId, tenantRole: role })
          }
          onRemoveFromCompany={(userId, companyId) =>
            removeCompanyMemberMutation.mutate({ companyId, userId })
          }
          canRemoveFromTenant={true}
          onRemoveFromTenant={(userId) => removeClientMemberMutation.mutate(userId)}
          onInviteEmployeeClick={() => companyManagement.openInviteModal('client')}
        />
      </Flex>

      <TenantInviteModal
        open={companyManagement.inviteModalOpen}
        onCancel={companyManagement.closeInviteModal}
        onGenerate={handleGenerateInvite}
        contextTitle={inviteContextTitle}
        companyId={companyManagement.inviteCompanyId}
      />

      <CompanyModal
        open={companyManagement.companyModalOpen}
        loading={companyManagement.companySaving}
        company={companyManagement.editingCompany}
        onCancel={companyManagement.closeCompanyModal}
        onSubmit={companyManagement.saveCompany}
      />

      {addToCompanyUser && (
        <AddCompanyMemberModal
          open={!!addToCompanyUser}
          onCancel={() => setAddToCompanyUser(null)}
          clientId={clientId}
          clientMembers={users}
          companies={companies}
          initialUserId={addToCompanyUser.id}
          onSuccess={() => setAddToCompanyUser(null)}
        />
      )}
    </div>
  );
}
